import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import chroma from 'chroma-js';
import { getCookie, setCookie } from '@tanstack/react-start/server';

export const defaultColor = '#FF6F00';
export const baseHue = 63;

export const adjustToClosest = (hue: number, adjustment: number): number => {
    const adjustedPositive = (hue + adjustment + 360) % 360;
    const adjustedNegative = (hue - adjustment + 360) % 360;

    const circularDistance = (a: number, b: number) =>
        Math.min(Math.abs(a - b), 360 - Math.abs(a - b));

    const distancePositive = circularDistance(adjustedPositive, baseHue);
    const distanceNegative = circularDistance(adjustedNegative, baseHue);

    let closestAdjustment =
        distancePositive <= distanceNegative
            ? adjustedPositive
            : adjustedNegative;

    if (
        closestAdjustment === adjustedPositive &&
        hue < baseHue &&
        adjustedPositive > baseHue
    ) {
        closestAdjustment = baseHue;
    } else if (
        closestAdjustment === adjustedNegative &&
        hue > baseHue &&
        adjustedNegative < baseHue
    ) {
        closestAdjustment = baseHue;
    }

    return closestAdjustment;
};

export type ThemeVars = {
    '--primary': string;
    '--hue-rot': string;
    '--brightness': string;
    '--saturation': string;
    '--primary-light': string;
    '--primary-foreground': string;
    '--accent': string;
};

export function computeThemeVars(color: string): ThemeVars {
    const primaryColor = chroma.valid(color)
        ? chroma(color)
        : chroma(defaultColor);
    const [hRaw, sRaw, lRaw] = primaryColor.hsl();
    const defaultHue = chroma(defaultColor).hsl()[0];
    const h = !Number.isNaN(hRaw) ? hRaw : defaultHue;
    const s = (sRaw ?? 1) * 100;
    const l = (lRaw ?? 0.5) * 100;
    const primaryForeground =
        chroma.contrast(primaryColor, '#ffffff') < 2
            ? '0, 0%, 0%'
            : '0, 0%, 100%';

    return {
        '--primary': `${h}, ${s}%, ${l}%`,
        '--hue-rot': `${h - defaultHue}deg`,
        '--brightness': `${l * 2}%`,
        '--saturation': `${s}%`,
        '--primary-light': `${h}, ${s}%, ${l}%, 0.2`,
        '--primary-foreground': primaryForeground,
        '--accent': `${h}, ${s}%, ${Math.max(l - 5, 0)}%`,
    };
}

function themeVarsToCSS(vars: ThemeVars): string {
    const body = Object.entries(vars)
        .map(([k, v]) => `${k}:${v};`)
        .join('');
    return `:root{${body}}`;
}

export function getThemeVarsForSSR(color: string) {
    return themeVarsToCSS(computeThemeVars(color));
}

export function applyThemeVars(root: HTMLElement, vars: ThemeVars) {
    for (const [k, v] of Object.entries(vars)) {
        root.style.setProperty(k, v);
    }
}

export const setPrimaryColorCookie = createServerFn({ method: 'POST' })
    .validator(
        z.object({
            color: z.string().regex(/^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/),
        }),
    )
    .handler(async (context) => {
        setCookie('primaryColor', context.data.color, {
            path: '/',
            maxAge: 60 * 60 * 24 * 30,
        });
    });

export const getPrimaryColorCookie = createServerFn({ method: 'GET' }).handler(
    async () => {
        return getCookie('primaryColor') ?? defaultColor;
    },
);
